// src/components/live/LiveSessionPlayer.js - Enhanced with Real YouTube Integration
import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FaVideo, FaPlay, FaPause, FaStop, FaVolumeUp, FaVolumeMute, 
  FaExpand, FaCompress, FaTimes, FaHeart, FaShare, FaUsers,
  FaClock, FaTrophy, FaComment, FaExternalLinkAlt, FaBroadcastTower,
  FaEye, FaThumbsUp, FaPaperPlane, FaCircle
} from 'react-icons/fa';
import toast from 'react-hot-toast';

const LiveSessionPlayer = ({ 
  session, 
  isOpen, 
  onClose, 
  likes = 0, 
  onLike, 
  onShare 
}) => {
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasLiked, setHasLiked] = useState(false);
  const [currentLikes, setCurrentLikes] = useState(likes);
  const [viewerCount, setViewerCount] = useState(0);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [videoStats, setVideoStats] = useState({
    viewCount: 0,
    likeCount: 0,
    commentCount: 0,
    subscriberCount: 0
  });

  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const chatContainerRef = useRef(null);
  const youtubePlayerRef = useRef(null);

  // Extract video ID and platform from stream URL
  const getVideoInfo = (url) => {
    if (!url) return { platform: null, videoId: null, embedUrl: null };

    // YouTube patterns - more comprehensive
    const youtubeRegex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/|youtube\.com\/live\/)([^"&?\/\s]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      return {
        platform: 'youtube',
        videoId: youtubeMatch[1],
        embedUrl: `https://www.youtube.com/embed/${youtubeMatch[1]}?autoplay=1&rel=0&modestbranding=1&enablejsapi=1&origin=${window.location.origin}`
      };
    }

    // Facebook patterns
    const facebookRegex = /(?:facebook\.com\/.*\/videos\/|fb\.watch\/)(\d+)/;
    const facebookMatch = url.match(facebookRegex);
    if (facebookMatch) {
      return {
        platform: 'facebook',
        videoId: facebookMatch[1],
        embedUrl: `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false&autoplay=true&width=800&height=450`
      };
    }

    // Twitch patterns
    const twitchRegex = /twitch\.tv\/([a-zA-Z0-9_]+)/;
    const twitchMatch = url.match(twitchRegex);
    if (twitchMatch) {
      return {
        platform: 'twitch',
        videoId: twitchMatch[1],
        embedUrl: `https://player.twitch.tv/?channel=${twitchMatch[1]}&parent=${window.location.hostname}&autoplay=true`
      };
    }

    // Generic video URL
    const videoExtensions = /\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i;
    if (videoExtensions.test(url)) {
      return {
        platform: 'video',
        videoId: null,
        embedUrl: url
      };
    }

    // Default to generic embed
    return {
      platform: 'generic',
      videoId: null,
      embedUrl: url
    };
  };

  const videoInfo = getVideoInfo(session?.streamUrl);

  // Fetch YouTube video statistics using YouTube Data API v3
  const fetchYouTubeStats = async (videoId) => {
    try {
      // You'll need to set up YouTube Data API key in your environment
      const API_KEY = process.env.REACT_APP_YOUTUBE_API_KEY;
      if (!API_KEY) {
        return;
      }

      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=statistics,liveStreamingDetails&id=${videoId}&key=${API_KEY}`
      );
      
      const data = await response.json();
      
      if (data.items && data.items.length > 0) {
        const item = data.items[0];
        const stats = item.statistics;
        const liveDetails = item.liveStreamingDetails;
        
        setVideoStats({
          viewCount: parseInt(stats.viewCount || 0),
          likeCount: parseInt(stats.likeCount || 0),
          commentCount: parseInt(stats.commentCount || 0),
          subscriberCount: 0 // This requires channel data
        });

        if (liveDetails && liveDetails.concurrentViewers) {
          setViewerCount(parseInt(liveDetails.concurrentViewers));
        }
      }
    } catch (error) {
    }
  };

  // Simulate live chat updates (replace with real chat API)
  const simulateLiveChat = () => {
    const sampleComments = [
      { user: 'DebateFan2024', message: 'Amazing argument structure!' },
      { user: 'SchoolChampion', message: 'The rebuttals are so strong' },
      { user: 'TeacherMike', message: 'Great educational content' },
      { user: 'StudentLeader', message: 'This is inspiring!' },
      { user: 'ParentSupport', message: 'So proud of these young debaters' },
      { user: 'CoachSarah', message: 'Excellent technique demonstration' },
      { user: 'FutureDebater', message: 'Learning so much from this' }
    ];

    const addRandomComment = () => {
      const randomComment = sampleComments[Math.floor(Math.random() * sampleComments.length)];
      const newComment = {
        id: Date.now() + Math.random(),
        user: randomComment.user,
        message: randomComment.message,
        timestamp: new Date(),
        isLive: true
      };
      
      setComments(prev => [newComment, ...prev.slice(0, 49)]); // Keep only last 50 comments
    };

    return setInterval(addRandomComment, Math.random() * 5000 + 3000); // Random interval 3-8 seconds
  };

  // Initialize stats and chat when modal opens
  useEffect(() => {
    if (isOpen && videoInfo.platform === 'youtube' && videoInfo.videoId) {
      fetchYouTubeStats(videoInfo.videoId);
      
      // Set up periodic stats updates
      const statsInterval = setInterval(() => {
        fetchYouTubeStats(videoInfo.videoId);
      }, 30000); // Update every 30 seconds

      // Start live chat simulation
      const chatInterval = simulateLiveChat();
      
      // Update viewer count periodically
      const viewerInterval = setInterval(() => {
        setViewerCount(prev => prev + Math.floor(Math.random() * 10 - 5)); // Simulate viewer changes
      }, 10000);

      return () => {
        clearInterval(statsInterval);
        clearInterval(chatInterval);
        clearInterval(viewerInterval);
      };
    }
  }, [isOpen, videoInfo.videoId, videoInfo.platform]);

  // Handle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = 0; // Since newest comments are at top
    }
  }, [comments]);

  // Handle like
  const handleLike = () => {
    if (!hasLiked) {
      setCurrentLikes(prev => prev + 1);
      setVideoStats(prev => ({ ...prev, likeCount: prev.likeCount + 1 }));
      setHasLiked(true);
      onLike?.();
      toast.success('Liked the session!');
    }
  };

  // Handle share
  const handleShare = async () => {
    const shareData = {
      title: session.title,
      text: `Watch ${session.title} live on ACD!`,
      url: session.streamUrl || window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        toast.success('Link copied to clipboard!');
      }
      onShare?.();
    } catch (error) {
      toast.error('Failed to share');
    }
  };

  // Add comment to live chat
  const handleAddComment = (e) => {
    e.preventDefault();
    if (newComment.trim()) {
      const comment = {
        id: Date.now(),
        user: 'You',
        message: newComment.trim(),
        timestamp: new Date(),
        isOwn: true
      };
      setComments(prev => [comment, ...prev]);
      setVideoStats(prev => ({ ...prev, commentCount: prev.commentCount + 1 }));
      setNewComment('');
      toast.success('Comment sent!');
    }
  };

  // Open in external tab
  const openInNewTab = () => {
    if (session?.streamUrl) {
      window.open(session.streamUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Format numbers for display
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  if (!isOpen || !session) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50 p-2"
      onClick={() => onClose()}
    >
      <motion.div
        ref={containerRef}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={`bg-gray-900 rounded-2xl overflow-hidden shadow-2xl border border-gray-700 ${
          isFullscreen 
            ? 'w-full h-full rounded-none border-none' 
            : 'w-full max-w-7xl h-full max-h-[95vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Video Player Area */}
        <div className={`relative bg-black ${isFullscreen ? 'h-full' : 'h-3/5'}`}>
          
          {/* Video Content */}
          {session.streamUrl && videoInfo.platform ? (
            <div className="w-full h-full relative">
              {videoInfo.platform === 'youtube' && (
                <iframe
                  ref={playerRef}
                  src={videoInfo.embedUrl}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  title={session.title}
                />
              )}

              {videoInfo.platform === 'facebook' && (
                <iframe
                  ref={playerRef}
                  src={videoInfo.embedUrl}
                  className="w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  title={session.title}
                />
              )}

              {videoInfo.platform === 'twitch' && (
                <iframe
                  ref={playerRef}
                  src={videoInfo.embedUrl}
                  className="w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  title={session.title}
                />
              )}

              {videoInfo.platform === 'video' && (
                <video
                  ref={playerRef}
                  src={videoInfo.embedUrl}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay={isPlaying}
                  muted={isMuted}
                />
              )}

              {videoInfo.platform === 'generic' && (
                <iframe
                  ref={playerRef}
                  src={session.streamUrl}
                  className="w-full h-full"
                  frameBorder="0"
                  allowFullScreen
                  title={session.title}
                />
              )}
            </div>
          ) : (
            // Fallback content when no stream URL
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-800 to-gray-900">
              <div className="text-center text-white">
                <FaVideo className="w-20 h-20 mx-auto mb-6 opacity-50" />
                <h3 className="text-3xl font-bold mb-3">{session.title}</h3>
                <p className="text-gray-300 mb-6 text-lg">Live Session Stream</p>
                <div className="flex items-center justify-center space-x-6 text-lg">
                  <span className="bg-red-500 px-4 py-2 rounded-full font-bold animate-pulse flex items-center">
                    <FaCircle className="w-3 h-3 mr-2 animate-pulse" />
                    LIVE
                  </span>
                  <span className="flex items-center">
                    <FaEye className="w-5 h-5 mr-2" />
                    {formatNumber(viewerCount || session.participants?.length || 0)} watching
                  </span>
                </div>
                
                {session.streamUrl && (
                  <button
                    onClick={openInNewTab}
                    className="mt-6 flex items-center space-x-3 mx-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors text-lg font-medium"
                  >
                    <FaExternalLinkAlt className="w-5 h-5" />
                    <span>Open in New Tab</span>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Live Stats Overlay */}
          {videoInfo.platform === 'youtube' && (
            <div className="absolute top-4 left-4 flex items-center space-x-4">
              <div className="bg-black/70 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center space-x-2">
                <FaCircle className="w-3 h-3 text-red-500 animate-pulse" />
                <span className="text-white font-bold text-sm">LIVE</span>
              </div>
              <div className="bg-black/70 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center space-x-2">
                <FaEye className="w-4 h-4 text-white" />
                <span className="text-white font-medium text-sm">
                  {formatNumber(viewerCount)} watching
                </span>
              </div>
              <div className="bg-black/70 backdrop-blur-sm px-3 py-2 rounded-lg flex items-center space-x-2">
                <FaThumbsUp className="w-4 h-4 text-white" />
                <span className="text-white font-medium text-sm">
                  {formatNumber(videoStats.likeCount)}
                </span>
              </div>
            </div>
          )}

          {/* Video Controls Overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6">
            <div className="flex items-center justify-between text-white">
              {/* Left Controls */}
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-3">
                  <span className="bg-red-500 px-3 py-1.5 rounded-full text-sm font-bold animate-pulse flex items-center">
                    <FaCircle className="w-2 h-2 mr-2" />
                    LIVE
                  </span>
                  <span className="text-sm font-medium">
                    {formatNumber(viewerCount || session.participants?.length || 0)} viewers
                  </span>
                </div>
              </div>
              
              {/* Right Controls */}
              <div className="flex items-center space-x-3">
                <button 
                  onClick={handleLike}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-300 ${
                    hasLiked 
                      ? 'bg-red-500 text-white transform scale-110' 
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                >
                  <FaHeart className={`w-4 h-4 ${hasLiked ? 'animate-pulse' : ''}`} />
                  <span className="text-sm font-medium">{formatNumber(currentLikes)}</span>
                </button>
                
                <button 
                  onClick={handleShare}
                  className="p-3 hover:bg-white/20 rounded-full transition-colors"
                  title="Share"
                >
                  <FaShare className="w-4 h-4" />
                </button>

                {session.streamUrl && (
                  <button 
                    onClick={openInNewTab}
                    className="p-3 hover:bg-white/20 rounded-full transition-colors"
                    title="Open in new tab"
                  >
                    <FaExternalLinkAlt className="w-4 h-4" />
                  </button>
                )}
                
                <button 
                  onClick={toggleFullscreen}
                  className="p-3 hover:bg-white/20 rounded-full transition-colors"
                  title="Toggle fullscreen"
                >
                  {isFullscreen ? <FaCompress className="w-4 h-4" /> : <FaExpand className="w-4 h-4" />}
                </button>
                
                <button 
                  onClick={onClose}
                  className="p-3 hover:bg-white/20 rounded-full transition-colors"
                  title="Close"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Session Info & Live Chat (only shown when not fullscreen) */}
        {!isFullscreen && (
          <div className="h-2/5 bg-white flex border-t border-gray-200">
            {/* Session Details */}
            <div className="flex-1 p-6 border-r border-gray-200 bg-gray-50">
              <div className="mb-4">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">{session.title}</h2>
                <p className="text-gray-600 mb-4 leading-relaxed">{session.description}</p>
              </div>
              
              {/* Session Stats */}
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center text-gray-600 bg-white p-3 rounded-lg border">
                  <FaUsers className="w-5 h-5 mr-3 text-blue-500" />
                  <div>
                    <div className="font-semibold text-gray-900">{session.participants?.length || 0}/{session.maxParticipants}</div>
                    <div className="text-sm">Participants</div>
                  </div>
                </div>
                <div className="flex items-center text-gray-600 bg-white p-3 rounded-lg border">
                  <FaClock className="w-5 h-5 mr-3 text-green-500" />
                  <div>
                    <div className="font-semibold text-gray-900">{session.timePerRound} min</div>
                    <div className="text-sm">Per Round</div>
                  </div>
                </div>
                <div className="flex items-center text-gray-600 bg-white p-3 rounded-lg border">
                  <FaTrophy className="w-5 h-5 mr-3 text-yellow-500" />
                  <div>
                    <div className="font-semibold text-gray-900">Round {session.currentRound || 1}/{session.roundsCount}</div>
                    <div className="text-sm">Current Round</div>
                  </div>
                </div>
                <div className="flex items-center text-gray-600 bg-white p-3 rounded-lg border">
                  <FaBroadcastTower className="w-5 h-5 mr-3 text-purple-500" />
                  <div>
                    <div className="font-semibold text-gray-900 capitalize">{session.category}</div>
                    <div className="text-sm">Category</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Live Chat */}
            <div className="w-96 bg-white flex flex-col border-l border-gray-200">
              <div className="p-4 border-b border-gray-200 bg-gray-50">
                <h3 className="font-bold text-gray-900 flex items-center text-lg">
                  <FaComment className="w-5 h-5 mr-3 text-green-600" />
                  Live Chat
                  <span className="ml-2 bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">
                    {comments.length} messages
                  </span>
                </h3>
              </div>
              
              {/* Messages */}
              <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
                {comments.length > 0 ? (
                  comments.map((comment) => (
                    <div key={comment.id} className={`flex items-start space-x-3 ${comment.isOwn ? 'flex-row-reverse space-x-reverse' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        comment.isOwn 
                          ? 'bg-green-500' 
                          : 'bg-gradient-to-br from-blue-500 to-purple-600'
                      }`}>
                        <span className="text-white text-xs font-bold">
                          {comment.user[0].toUpperCase()}
                        </span>
                      </div>
                      <div className={`flex-1 min-w-0 ${comment.isOwn ? 'text-right' : ''}`}>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-bold text-gray-900 truncate">
                            {comment.user}
                          </span>
                          <span className="text-xs text-gray-500">
                            {comment.timestamp ? new Date(comment.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'now'}
                          </span>
                        </div>
                        <div className={`mt-1 px-3 py-2 rounded-lg ${
                          comment.isOwn 
                            ? 'bg-green-100 text-green-900 ml-8' 
                            : 'bg-white border mr-8'
                        }`}>
                          <p className="text-sm break-words">{comment.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <FaComment className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No messages yet. Be the first to comment!</p>
                  </div>
                )}
              </div>
              
              {/* Message Input */}
              <form onSubmit={handleAddComment} className="p-4 border-t border-gray-200 bg-white">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    maxLength={200}
                  />
                  <button
                    type="submit"
                    disabled={!newComment.trim()}
                    className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    <FaPaperPlane className="w-4 h-4" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default LiveSessionPlayer;